/**
 * EquipmentDowntime Component
 * US-013D Phase 2: Equipment downtime logging and impact assessment
 *
 * Features:
 * - Form to log equipment downtime
 * - List of current/upcoming downtime
 * - Impact assessment panel
 */

import { useState } from 'react'
import { toLocalDateString } from '@/utils/dateUtils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import {
  AlertTriangle,
  Clock,
  Loader2,
  Plus,
  Wrench,
  Calendar,
  ArrowRight,
  Package,
  AlertCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'

// =============================================================================
// Downtime Reason Options
// =============================================================================

const DOWNTIME_REASONS = [
  { value: 'maintenance', label: 'Scheduled Maintenance' },
  { value: 'breakdown', label: 'Equipment Breakdown' },
  { value: 'repair', label: 'Repair Required' },
  { value: 'cleaning', label: 'Deep Cleaning' },
  { value: 'calibration', label: 'Calibration' },
  { value: 'upgrade', label: 'Upgrade/Modification' },
  { value: 'other', label: 'Other' }
]

// =============================================================================
// Downtime Form Dialog
// =============================================================================

function DowntimeFormDialog({
  open,
  onOpenChange,
  resources,
  onSubmit,
  submitting
}) {
  const [formData, setFormData] = useState({
    resource_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    notes: ''
  })
  const [customReason, setCustomReason] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()

    const data = {
      ...formData,
      reason: formData.reason === 'other' ? customReason : formData.reason,
      end_date: formData.end_date || undefined
    }

    await onSubmit(data)

    // Reset form
    setFormData({
      resource_id: '',
      start_date: '',
      end_date: '',
      reason: '',
      notes: ''
    })
    setCustomReason('')
  }

  const today = toLocalDateString(new Date())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Log Equipment Downtime
            </DialogTitle>
            <DialogDescription>
              Record equipment unavailability to track impact on production
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Resource Selection */}
            <div className="space-y-2">
              <Label htmlFor="resource">Resource</Label>
              <Select
                value={formData.resource_id}
                onValueChange={(value) => setFormData({ ...formData, resource_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select resource" />
                </SelectTrigger>
                <SelectContent>
                  {resources.map((resource) => (
                    <SelectItem key={resource.id} value={resource.id}>
                      {resource.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  min={today}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">
                  End Date
                  <span className="text-muted-foreground text-xs ml-1">(optional)</span>
                </Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  min={formData.start_date || today}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for ongoing downtime
                </p>
              </div>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Select
                value={formData.reason}
                onValueChange={(value) => setFormData({ ...formData, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {DOWNTIME_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {formData.reason === 'other' && (
                <Input
                  placeholder="Describe the reason..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  required
                />
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">
                Additional Notes
                <span className="text-muted-foreground text-xs ml-1">(optional)</span>
              </Label>
              <Textarea
                id="notes"
                placeholder="Any additional details..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !formData.resource_id || !formData.start_date || !formData.reason}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Logging...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Log Downtime
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Downtime List Item
// =============================================================================

function DowntimeListItem({ downtime, onAssessImpact }) {
  const isOngoing = !downtime.end_date
  const isPast = downtime.end_date && new Date(downtime.end_date) < new Date()

  return (
    <div
      className={cn(
        'p-4 rounded-lg border',
        isOngoing && 'border-amber-300 bg-amber-50',
        isPast && 'border-gray-200 bg-gray-50',
        !isOngoing && !isPast && 'border-blue-200 bg-blue-50'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="font-medium truncate">
              {downtime.production_resources?.name || 'Unknown Resource'}
            </span>
            {isOngoing && (
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                Ongoing
              </Badge>
            )}
          </div>

          <p className="text-sm text-muted-foreground mt-1 capitalize">
            {downtime.reason?.replace(/_/g, ' ')}
          </p>

          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>
              {new Date(downtime.start_date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short'
              })}
            </span>
            <ArrowRight className="h-3 w-3" />
            <span>
              {downtime.end_date
                ? new Date(downtime.end_date).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short'
                  })
                : 'TBD'
              }
            </span>
          </div>

          {downtime.impact_assessment && (
            <p className="text-xs text-muted-foreground mt-2 italic">
              {downtime.impact_assessment}
            </p>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onAssessImpact(downtime.id)}
        >
          <AlertTriangle className="h-4 w-4 mr-1" />
          Impact
        </Button>
      </div>
    </div>
  )
}

// =============================================================================
// Impact Assessment Panel
// =============================================================================

function ImpactAssessmentPanel({ impact, onClose }) {
  if (!impact) return null

  return (
    <Card className="mt-4 border-amber-300">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Impact Assessment
            </CardTitle>
            <CardDescription>
              {impact.resource_name}: {impact.start_date} to {impact.end_date || 'ongoing'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary */}
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{impact.summary}</AlertDescription>
        </Alert>

        {/* Affected Batches */}
        {impact.affected_batches && impact.affected_batches.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Affected Batches ({impact.affected_batches.length})
            </h4>
            <div className="space-y-2">
              {impact.affected_batches.map((batch, idx) => (
                <div
                  key={idx}
                  className="p-2 bg-muted rounded-md flex items-center justify-between text-sm"
                >
                  <div>
                    <span className="font-medium">{batch.name}</span>
                    <span className="text-muted-foreground ml-2">
                      ({batch.quantity} blocks)
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {batch.dates_affected.length} day{batch.dates_affected.length !== 1 ? 's' : ''} affected
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reschedule Options */}
        {impact.reschedule_options && impact.reschedule_options.length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-2">Reschedule Options</h4>
            <div className="space-y-2">
              {impact.reschedule_options.map((option, idx) => (
                <div
                  key={idx}
                  className="p-3 border rounded-md"
                >
                  <p className="font-medium text-sm capitalize">{option.option}</p>
                  <p className="text-sm text-muted-foreground">{option.description}</p>
                  <p className="text-xs text-amber-600 mt-1">{option.impact}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {impact.affected_batches?.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No batches affected during this downtime period</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function EquipmentDowntime({
  resources,
  downtimes,
  loading,
  onCreateDowntime,
  onAssessImpact
}) {
  const [formOpen, setFormOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedImpact, setSelectedImpact] = useState(null)
  const [assessing, setAssessing] = useState(false)

  const handleSubmit = async (data) => {
    setSubmitting(true)
    try {
      await onCreateDowntime(data)
      setFormOpen(false)
    } catch (err) {
      console.error('Failed to create downtime:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleAssessImpact = async (downtimeId) => {
    setAssessing(true)
    try {
      const impact = await onAssessImpact(downtimeId)
      setSelectedImpact(impact)
    } catch (err) {
      console.error('Failed to assess impact:', err)
    } finally {
      setAssessing(false)
    }
  }

  // Separate ongoing (scheduledDowntimes available for future filtering features)
  const ongoingDowntimes = downtimes.filter(d => !d.end_date)
  // const scheduledDowntimes = downtimes.filter(d => d.end_date && new Date(d.end_date) >= new Date())

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            Equipment Downtime
          </h3>
          <p className="text-sm text-muted-foreground">
            Track equipment unavailability and production impact
          </p>
        </div>

        <Button onClick={() => setFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Log Downtime
        </Button>
      </div>

      {/* Ongoing Downtimes Alert */}
      {ongoingDowntimes.length > 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Ongoing Downtime</AlertTitle>
          <AlertDescription className="text-amber-700">
            {ongoingDowntimes.length} resource{ongoingDowntimes.length !== 1 ? 's' : ''} currently unavailable
          </AlertDescription>
        </Alert>
      )}

      {/* Downtime List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Current & Upcoming</CardTitle>
          <CardDescription>
            {downtimes.length} downtime record{downtimes.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : downtimes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No downtime records</p>
              <p className="text-sm mt-1">All equipment is operational</p>
            </div>
          ) : (
            <div className="space-y-3">
              {downtimes.map((downtime) => (
                <DowntimeListItem
                  key={downtime.id}
                  downtime={downtime}
                  onAssessImpact={handleAssessImpact}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Impact Assessment Panel */}
      {assessing && (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Assessing impact...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedImpact && !assessing && (
        <ImpactAssessmentPanel
          impact={selectedImpact}
          onClose={() => setSelectedImpact(null)}
        />
      )}

      {/* Downtime Form Dialog */}
      <DowntimeFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        resources={resources}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    </div>
  )
}
